import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BarChart3, RotateCcw, TrendingUp, Truck } from 'lucide-react'
import { get } from '@/lib/api'
import { money, num, percent } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useViewportMode } from '@/hooks/useViewportMode'
import { LoadError } from '@/components/ui/data-notice'
import { KpiTile } from '@/components/ui/kpi-tile'
import { PageHeader } from '@/components/ui/page-header'
import { Section } from '@/components/ui/section'
import { INDEX_CELL, INDEX_HEADER, IndexBadge } from '@/components/ui/data-table'
import { Segmented, PERIOD_OPTIONS } from '@/components/ui/segmented'
import { DailyBars } from '@/components/charts/DailyBars'
import { Donut } from '@/components/charts/Donut'
import { RankBars } from '@/components/charts/RankBars'
import { Sparkline } from '@/components/charts/Sparkline'
import { SplitMeter } from '@/components/charts/SplitMeter'
import { compactMoney, countFormat, fillDaily } from '@/components/charts/chart-utils'
import { statusSegments } from '@/components/charts/statusSegments'

interface RevenueReport {
  days: number
  totals: {
    revenue: number
    cost: number
    shippingCost: number
    returnedShippingLoss: number
    netProfit: number
    missingCarrierFeeCount?: number
    missingReturnFeeCount?: number
    netMarginPct: number
    count: number
    deliveredCount: number
    deliveringCount: number
    pendingCount: number
    returnedCount: number
    deliveryRate: number
    returnRate: number
    aov: number
  }
  daily: Array<{
    date: string
    revenue: number
    cost: number
    shippingCost: number
    count: number
    deliveredCount: number
    returnedCount: number
  }>
  byChannel: Array<{ channel: string; revenue: number; count: number; deliveredCount?: number; returnedCount?: number }>
  byCarrier: Array<{ carrierId: string; count: number; deliveredCount?: number; returnedCount?: number }>
  byRegion: Array<{ province: string; count: number; revenue: number; returnedCount?: number }>
  topProducts: Array<{
    product: { sku: string; name: string } | null
    count: number
    revenue: number
    returnedCount: number
    returnRate: number
  }>
}

/** Mức hoàn của một SKU đáng để ý: ≥15% và ít nhất 2 đơn hoàn (tránh báo động vì 1 đơn lẻ). */
const isHighReturn = (p: { returnRate: number; returnedCount: number }) => p.returnRate >= 0.15 && p.returnedCount >= 2

export function ShopReportView({ isSubView = false }: { isSubView?: boolean }) {
  const mobile = useViewportMode() === 'mobile'
  const [days, setDays] = useState<number>(30)
  const [metric, setMetric] = useState<'orders' | 'revenue'>('orders')

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['reports.revenue', days],
    queryFn: () => get<RevenueReport>(`/reports/revenue?days=${days}`),
  })

  const t = data?.totals
  const dailyOrders = fillDaily(data?.daily, days, (d) => d.count)
  const dailyRevenue = fillDaily(data?.daily, days, (d) => d.revenue)
  const dailyReturned = fillDaily(data?.daily, days, (d) => d.returnedCount)
  const missingCarrier = t?.missingCarrierFeeCount ?? 0
  // Doanh thu không tính đơn hoàn → số đơn và giá trị trung bình cũng theo đúng tập đó
  // (trước đây ghi tổng số đơn nhưng lại chia trung bình cho số đơn không hoàn).
  const revenueOrders = Math.max(0, (t?.count ?? 0) - (t?.returnedCount ?? 0))
  const avgOrderValue = revenueOrders > 0 ? Math.round((t?.revenue ?? 0) / revenueOrders) : 0
  const loading = isLoading && !data

  const period = (
    <Segmented label="Chọn kỳ báo cáo" value={days} onChange={setDays} options={PERIOD_OPTIONS} />
  )

  return (
    <div className={cn('space-y-5', !isSubView && 'mx-auto max-w-7xl')}>
      {isSubView ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2>Đơn do chính bạn bán</h2>
          {period}
        </div>
      ) : (
        <PageHeader title="Báo cáo" actions={period} />
      )}

      {isError ? (
        <Section>
          <LoadError onRetry={() => refetch()}>Không tải được báo cáo.</LoadError>
        </Section>
      ) : (
        <>

          <div className="grid grid-cols-2 gap-3 min-[1200px]:grid-cols-4">
            <KpiTile
              tone="blue"
              icon={BarChart3}
              label="COD dự kiến"
              value={money(t?.revenue ?? 0)}
              hint={`${countFormat.format(revenueOrders)} đơn không hoàn · TB ${money(avgOrderValue)}/đơn`}
              loading={loading}
            >
              <Sparkline values={dailyRevenue.map((d) => d.value)} label={`COD dự kiến từng ngày trong ${days} ngày`} />
            </KpiTile>
            <KpiTile
              tone="green"
              icon={TrendingUp}
              label="Lãi ròng"
              value={<span className={(t?.netProfit ?? 0) < 0 ? 'text-danger' : undefined}>{money(t?.netProfit ?? 0)}</span>}
              hint={
                missingCarrier > 0
                  ? `Chưa trừ cước ${num(missingCarrier)} đơn`
                  : `Biên lãi ${percent(t?.netMarginPct ?? 0)}`
              }
              loading={loading}
            >
              <SplitMeter
                parts={[
                  { label: 'Giá vốn', value: t?.cost ?? 0, barClass: 'bg-toneFg/25' },
                  { label: 'Cước', value: t?.shippingCost ?? 0, barClass: 'bg-toneFg/50' },
                  { label: 'Lãi', value: Math.max(0, t?.netProfit ?? 0), barClass: 'bg-toneFg' },
                ]}
                format={compactMoney}
                textClass="text-toneFg"
              />
            </KpiTile>
            <KpiTile
              tone="violet"
              icon={Truck}
              label="Tỷ lệ giao thành công"
              value={percent(t?.deliveryRate ?? 0)}
              hint={`${countFormat.format(t?.deliveredCount ?? 0)}/${countFormat.format(t?.count ?? 0)} đơn`}
              loading={loading}
            >
              <SplitMeter
                parts={[
                  { label: 'Đã giao', value: t?.deliveredCount ?? 0, barClass: 'bg-toneFg' },
                  {
                    label: 'Còn lại',
                    value: Math.max(0, (t?.count ?? 0) - (t?.deliveredCount ?? 0)),
                    barClass: 'bg-toneFg/25',
                  },
                ]}
                format={(n) => countFormat.format(n)}
                textClass="text-toneFg"
              />
            </KpiTile>
            <KpiTile
              tone="orange"
              icon={RotateCcw}
              label="Tỷ lệ hoàn"
              value={percent(t?.returnRate ?? 0)}
              hint={
                (t?.returnedShippingLoss ?? 0) > 0
                  ? `${countFormat.format(t?.returnedCount ?? 0)} đơn hoàn · cước đơn hoàn ${money(t?.returnedShippingLoss)}`
                  : `${countFormat.format(t?.returnedCount ?? 0)} đơn hoàn`
              }
              loading={loading}
            >
              <Sparkline values={dailyReturned.map((d) => d.value)} label={`Số đơn hoàn từng ngày trong ${days} ngày`} />
            </KpiTile>
          </div>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]">
            <Section
              title={`${days} ngày qua`}
              action={
                <Segmented
                  label="Chọn số liệu"
                  value={metric}
                  onChange={setMetric}
                  options={[
                    ['orders', 'Số đơn'],
                    ['revenue', 'COD dự kiến'],
                  ]}
                />
              }
            >
              <div className={cn('p-4 pt-5 transition-opacity', loading && 'opacity-50')}>
                {metric === 'orders' ? (
                  <DailyBars
                    data={dailyOrders}
                    format={(n) => `${countFormat.format(n)} đơn`}
                    tickFormat={(n) => countFormat.format(n)}
                    label="Số đơn mỗi ngày"
                    integer
                  />
                ) : (
                  <DailyBars data={dailyRevenue} format={money} tickFormat={compactMoney} label="COD dự kiến mỗi ngày" />
                )}
                <p className="mt-3 text-xs text-muted">
                  Tổng {days} ngày: {countFormat.format(t?.count ?? 0)} đơn · {money(t?.revenue ?? 0)} COD dự kiến.
                </p>
              </div>
            </Section>
            <Section title="Trạng thái đơn" action={<span className="text-xs text-muted">{days} ngày qua</span>}>
              <div className="p-4">
                <Donut
                  emptyLabel="Chưa có đơn"
                  segments={statusSegments({
                    deliveredOrders: t?.deliveredCount ?? 0,
                    deliveringOrders: t?.deliveringCount ?? 0,
                    returnedOrders: t?.returnedCount ?? 0,
                    pendingOrders: t?.pendingCount ?? 0,
                  })}
                />
              </div>
            </Section>
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Section title="Kênh bán">
              {data?.byChannel?.length ? (
                <RankBars
                  rows={[...data.byChannel]
                    .sort((a, b) => b.revenue - a.revenue)
                    .map((c) => ({
                      key: c.channel,
                      label: c.channel || 'Khác',
                      meta: <span>{countFormat.format(c.count)} đơn</span>,
                      value: c.revenue,
                      display: compactMoney(c.revenue),
                    }))}
                />
              ) : (
                <p className="px-4 py-10 text-center text-sm text-muted">Chưa có đơn</p>
              )}
            </Section>
            <Section title="Tỉnh / thành nhận hàng">
              {data?.byRegion?.length ? (
                <RankBars
                  rows={[...data.byRegion]
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 8)
                    .map((r) => ({
                      key: r.province,
                      label: r.province || 'Chưa rõ',
                      meta: (
                        <>
                          <span>{compactMoney(r.revenue)}</span>
                          {(r.returnedCount ?? 0) > 0 && <span>· {countFormat.format(r.returnedCount ?? 0)} hoàn</span>}
                        </>
                      ),
                      value: r.count,
                      display: `${countFormat.format(r.count)} đơn`,
                    }))}
                />
              ) : (
                <p className="px-4 py-10 text-center text-sm text-muted">Chưa có đơn</p>
              )}
            </Section>
          </div>

          <Section title="Sản phẩm bán chạy">
            {data?.topProducts?.length && mobile ? (
              // Điện thoại: mỗi sản phẩm một dòng — bảng 4 cột phải kéo ngang
              <ul className="divide-y divide-rule">
                {data.topProducts.map((p, i) => (
                  <li key={p.product?.sku ?? i} className="space-y-1 px-4 py-3 text-sm">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="flex min-w-0 items-baseline gap-2">
                        <IndexBadge n={i + 1} />
                        <p className="min-w-0 truncate font-medium text-ink">{p.product?.name || 'Đơn không gắn sản phẩm'}</p>
                      </div>
                      <span className="shrink-0 font-semibold tabular-nums text-ink">{money(p.revenue)}</span>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-inkSoft">
                      <span>
                        {p.product?.sku && <span className="font-mono text-muted">{p.product.sku} · </span>}
                        Đã bán <span className="tabular-nums text-ink">{num(p.count)}</span>
                      </span>
                      <span className="tabular-nums">
                        Hoàn <ReturnCell p={p} />
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : data?.topProducts?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-sm">
                  <thead>
                    <tr className="border-b border-rule text-left text-xs text-muted">
                      <th className={cn('py-2.5 pl-4 pr-1 font-medium', INDEX_CELL)}>{INDEX_HEADER}</th>
                      <th className="px-4 py-2.5 font-medium">Sản phẩm</th>
                      <th className="px-4 py-2.5 text-right font-medium">Đã bán</th>
                      <th className="px-4 py-2.5 text-right font-medium">Doanh số</th>
                      <th className="px-4 py-2.5 text-right font-medium">Hoàn</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-rule">
                    {data.topProducts.map((p, i) => (
                      <tr key={p.product?.sku ?? i}>
                        <td className={cn('py-3 pl-4 pr-1', INDEX_CELL)}>{i + 1}</td>
                        <td className="max-w-xs px-4 py-3">
                          <p className="truncate font-medium text-ink">{p.product?.name || 'Đơn không gắn sản phẩm'}</p>
                          {p.product?.sku && <p className="font-mono text-xs text-muted">{p.product.sku}</p>}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-ink">{num(p.count)}</td>
                        <td className="px-4 py-3 text-right font-semibold tabular-nums text-ink">{money(p.revenue)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                          <ReturnCell p={p} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="px-4 py-10 text-center text-sm text-muted">Chưa có đơn gắn sản phẩm</p>
            )}
          </Section>
        </>
      )}
    </div>
  )
}

function ReturnCell({ p }: { p: { returnRate: number; returnedCount: number } }) {
  return isHighReturn(p) ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-danger/10 px-2 py-0.5 text-xs font-semibold text-danger">
      <RotateCcw className="size-3" aria-hidden />
      {percent(p.returnRate, 0)} · {p.returnedCount} đơn
    </span>
  ) : (
    <span className="text-muted">{p.returnedCount > 0 ? `${percent(p.returnRate, 0)} · ${p.returnedCount} đơn` : '—'}</span>
  )
}
